import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Viewer } from '../Viewer-Factory/Viewer';

const DEFAULT_FILL_CORNER_DEBUG_GROUP = 'defaultFillCornerDebug';
const DEBUG_FLAG = 'VITE_DEBUG_MODE';
export const ONEDRAW_DEBUGGER_VISIBILITY_EVENT = 'onedraw-debugger-visibility-changed';

export type FillDebuggerOptions = {
    showStartCorner: boolean;
    showAutofillAllowedAreas: boolean;
    showDefaultAllowedAreas: boolean;
    showRoofEdgeTypes: boolean;
    showBoundMeshBox: boolean;
    showObstructionCenters: boolean;
    showObstructionNodes: boolean;
    showRoofNodes: boolean;
    showTreeCylinders: boolean;
    showModuleCollisionObb: boolean;
    showWallObb: boolean;
    showObstructionRayArrows: boolean;
    logModulePlacementStatus: boolean;
};

export type ShadeWorkerMeshType = 'roof' | 'wall' | 'tree' | 'obst' | 'racking-module' | 'map';

export type ShadeWorkerSunVectorDebug = {
    enabled: boolean;
    roofFaceId: string;
    roofIdx: number;
    shadePointIdx: number;
    month: number;
    day: number;
    hour: number;
    allHours: boolean;
};

export type ShadeWorkerOptions = {
    renderRoof: boolean;
    renderWall: boolean;
    renderTree: boolean;
    renderObstruction: boolean;
    renderRackingModule: boolean;
    renderMap: boolean;
    drawSunVector: boolean;
    sunVectorRoofFaceId: string;
    sunVectorRoofIdx: number;
    sunVectorShadePointIdx: number;
    sunVectorMonth: number;
    sunVectorDay: number;
    sunVectorHour: number;
    sunVectorFullDay: boolean;
};

export type ShadeWorkerRenderMeshTypes = Record<ShadeWorkerMeshType, boolean>;

export function getShadeWorkerRenderMeshTypes(options: ShadeWorkerOptions): ShadeWorkerRenderMeshTypes {
    return {
        roof: options.renderRoof,
        wall: options.renderWall,
        tree: options.renderTree,
        obst: options.renderObstruction,
        'racking-module': options.renderRackingModule,
        map: options.renderMap,
    };
}

export function isShadeWorkerMeshRenderEnabled(options: ShadeWorkerOptions): boolean {
    return Object.values(getShadeWorkerRenderMeshTypes(options)).some(Boolean);
}

export type OneDrawDebuggerOptions = {
    fillDebugger: FillDebuggerOptions;
    shadeWorker: ShadeWorkerOptions;
};

const defaultDebuggerOptions: OneDrawDebuggerOptions = {
    fillDebugger: {
        showStartCorner: true,
        showAutofillAllowedAreas: true,
        showDefaultAllowedAreas: true,
        showRoofEdgeTypes: false,
        showBoundMeshBox: false,
        showObstructionCenters: false,
        showObstructionNodes: false,
        showRoofNodes: false,
        showTreeCylinders: false,
        showModuleCollisionObb: false,
        showWallObb: false,
        showObstructionRayArrows: false,
        logModulePlacementStatus: false,
    },
    shadeWorker: {
        renderRoof: false,
        renderWall: false,
        renderTree: false,
        renderObstruction: false,
        renderRackingModule: false,
        renderMap: false,
        drawSunVector: false,
        sunVectorRoofFaceId: '',
        sunVectorRoofIdx: 0,
        sunVectorShadePointIdx: 0,
        sunVectorMonth: 5,
        sunVectorDay: 20,
        sunVectorHour: 12,
        sunVectorFullDay: false,
    },
};

export function getShadeWorkerSunVectorDebug(options: ShadeWorkerOptions): ShadeWorkerSunVectorDebug | null {
    if (!options.drawSunVector || !options.sunVectorRoofFaceId) return null;
    return {
        enabled: true,
        roofFaceId: options.sunVectorRoofFaceId,
        roofIdx: options.sunVectorRoofIdx,
        shadePointIdx: options.sunVectorShadePointIdx,
        month: options.sunVectorMonth,
        day: options.sunVectorDay,
        hour: options.sunVectorHour,
        allHours: options.sunVectorFullDay,
    };
}

type DebugManagerFn = (self: Viewer, args?: any) => void;

export function DebugDrawPoint(self: Viewer, args: { position: THREE.Vector3; radius?: number; color?: string; group?: string; opacity?: number }) {
    const { position, radius, color, group, opacity } = args;
    const geometry = new THREE.SphereGeometry(radius ?? 0.2, 32, 32);
    const material = new THREE.MeshStandardMaterial({ color: color ?? 0xff0000 });

    if (opacity !== undefined && opacity < 1) {
        material.transparent = true;
        material.opacity = opacity;
    }

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.userData.debugDraw = true;
    mesh.userData.group = group;
    self.scene.add(mesh);
}

export function DebugDrawLine(self: Viewer, args: { start: THREE.Vector3; end: THREE.Vector3; radius?: number; radiusStart?: number; radiusEnd?: number; color?: string; group?: string; opacity?: number }) {
    const { start, end, radius, radiusStart, radiusEnd, color, group, opacity } = args;
    const endVector = toVector(end);

    const geometry = new THREE.CylinderGeometry(
        radiusEnd ?? radius ?? 0.025,
        radiusStart ?? radius ?? 0.025,
        toVector(start).sub(endVector).length()
    );
    const material = new THREE.MeshStandardMaterial({ color: color ?? 0xff0000 });

    if (opacity !== undefined && opacity < 1) {
        material.transparent = true;
        material.opacity = opacity;
    }

    const mesh = new THREE.Mesh(geometry, material);
    const pivot = new THREE.Group();
    pivot.userData.debugDraw = true;
    pivot.userData.group = group;
    pivot.add(mesh);

    pivot.position.copy(toVector(start).add(endVector).divideScalar(2));
    self.scene.add(pivot);

    pivot.lookAt(endVector);
    mesh.rotation.x = Math.PI / 2;
}

export function DebugDrawBox(self: Viewer, args: any) {
    let { position, scale, rotation, start, end, box, color, group, opacity } = args;

    if (box) {
        start = box.min;
        end = box.max;
    }

    if (start && end) {
        position = new THREE.Vector3((start.x + end.x) / 2, (start.y + end.y) / 2, (start.z + end.z) / 2);
        scale = new THREE.Vector3(end.x - start.x, end.y - start.y, end.z - start.z);
    }

    const scaleX = scale?.x ?? 1;
    const scaleY = scale?.y ?? 1;
    const scaleZ = scale?.z ?? 1;

    const shape = new THREE.Shape();
    shape.moveTo(-scaleX / 2, -scaleZ / 2);
    shape.lineTo(scaleX / 2, -scaleZ / 2);
    shape.lineTo(scaleX / 2, scaleZ / 2);
    shape.lineTo(-scaleX / 2, scaleZ / 2);

    const geometry = new THREE.ExtrudeGeometry(shape, { depth: scaleY, bevelEnabled: false });
    const material = new THREE.MeshStandardMaterial({ color: color ?? 0xff0000 });

    if (opacity !== undefined && opacity < 1) {
        material.transparent = true;
        material.opacity = opacity;
    }

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(position.x, position.y, position.z);

    if (rotation) {
        mesh.rotation.set(rotation.x + Math.PI / 2, rotation.z, rotation.y);
    } else {
        mesh.rotation.x = Math.PI / 2;
    }

    mesh.userData.debugDraw = true;
    mesh.userData.group = group;
    self.scene.add(mesh);
}

export function DebugDrawPolygon(self: Viewer, args: { points: THREE.Vector3[]; radius?: number; color?: string; group?: string; opacity?: number }) {
    const { points, radius, color, group, opacity } = args;

    for (let i = 1; i < points.length; i++) {
        DebugDrawLine(self, {
            start: points[i - 1],
            end: points[i],
            radius: radius,
            color: color,
            group: group,
            opacity: opacity,
        });
    }

    DebugDrawLine(self, {
        start: points[points.length - 1],
        end: points[0],
        radius: radius,
        color: color,
        group: group,
        opacity: opacity,
    });
}

export function DebugDrawLabel(self: Viewer, args: { text: string; position: THREE.Vector3; color?: string; fontSize?: string; group?: string }) {
    const { text, position, color, fontSize, group } = args;
    const element: any = document.createElement('pre');
    element.textContent = text;
    element.style.color = color;
    element.style.fontSize = fontSize;

    const label = new CSS2DObject(element);
    label.position.set(position.x, position.y, position.z);
    label.userData.debugDraw = true;
    label.userData.group = group;
    self.scene.add(label);
}

export function DebugDrawRelativeAxes(self: Viewer, args: { object: any; scale?: number; radius?: number; group?: string; opacity?: number }) {
    const { object, scale, radius, group, opacity } = args;

    const test = new THREE.Group();
    object.add(test);

    const objPosition = object.getWorldPosition(new THREE.Vector3());

    test.position.set(scale ?? 1, 0, 0);
    const xCoord = test.getWorldPosition(new THREE.Vector3());

    test.position.set(0, scale ?? 1, 0);
    const yCoord = test.getWorldPosition(new THREE.Vector3());

    test.position.set(0, 0, scale ?? 1);
    const zCoord = test.getWorldPosition(new THREE.Vector3());

    object.remove(test);

    DebugDrawLine(self, {
        start: objPosition,
        end: xCoord,
        color: '#ff0000',
        radius: radius,
        group: group,
        opacity: opacity,
    });

    DebugDrawLine(self, {
        start: objPosition,
        end: yCoord,
        color: '#00ff00',
        radius: radius,
        group: group,
        opacity: opacity,
    });

    DebugDrawLine(self, {
        start: objPosition,
        end: zCoord,
        color: '#0000ff',
        radius: radius,
        group: group,
        opacity: opacity,
    });
}

export function DebugDrawOBB(self: Viewer, args: { object: any; spacing?: number; color?: number; group?: string; opacity?: number; geometryObb?: boolean }) {
    const { object, spacing, color, group, opacity, geometryObb } = args;

    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshStandardMaterial({ color: color ?? 0xff0000 });

    if (opacity !== undefined && opacity < 1) {
        material.transparent = true;
        material.opacity = opacity;
    }

    const mesh = new THREE.Mesh(geometry, material);
    const obb = geometryObb
        ? object.geometry.userData.obb
        : object.userData.obb;

    object.updateWorldMatrix(true, false);
    mesh.applyMatrix4(object.matrixWorld);

    if (geometryObb) {
        mesh.position.add(obb.center);
    } else {
        mesh.position.copy(obb.center);
    }

    mesh.scale.set(obb.halfSize.x, obb.halfSize.y, obb.halfSize.z).multiplyScalar(2);

    if (spacing) {
        mesh.scale.addScalar(spacing);
    }

    mesh.userData.debugDraw = true;
    mesh.userData.group = group;
    self.scene.add(mesh);
}

export function ClearDebugDraw(self: Viewer, group: string) {
    if (!self?.scene?.children) return;
    const toRemove = [];

    for (const child of self.scene.children) {
        if (!child.userData.debugDraw) {
            continue;
        }

        if (!group || child.userData.group === group) {
            toRemove.push(child);
        }
    }

    for (const item of toRemove) {
        self.scene.remove(item);
    }
}

function toVector(v: { x: number; y: number; z: number }) {
    return new THREE.Vector3(v.x, v.y, v.z);
}

function clearDefaultFillCorner(self: Viewer, args?: { roofMesh?: any }) {
    const targetRoof = args?.roofMesh;
    if (targetRoof) {
        const old = targetRoof?.getObjectByName(DEFAULT_FILL_CORNER_DEBUG_GROUP);
        if (old) targetRoof.remove(old);
        return;
    }
    self.roofMeshArr?.forEach((roofMesh: any) => {
        const old = roofMesh?.getObjectByName(DEFAULT_FILL_CORNER_DEBUG_GROUP);
        if (old) roofMesh.remove(old);
    });
}

function renderDefaultFillStartCorner(self: Viewer, args: { roofMesh: any; pos2DArr: any[]; cornerKey: string }) {
    const { roofMesh, pos2DArr, cornerKey } = args || {};
    if (!roofMesh || !pos2DArr?.length || !cornerKey) return;

    const old = roofMesh?.getObjectByName(DEFAULT_FILL_CORNER_DEBUG_GROUP);
    if (old) roofMesh.remove(old);

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    pos2DArr.forEach((p: any) => {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.z < minZ) minZ = p.z;
        if (p.z > maxZ) maxZ = p.z;
    });
    const cornerMap: any = {
        topLeft: { x: minX, z: maxZ },
        topRight: { x: maxX, z: maxZ },
        bottomLeft: { x: minX, z: minZ },
        bottomRight: { x: maxX, z: minZ },
    };
    const corner = cornerMap[cornerKey];
    if (!corner) return;

    const group = new THREE.Group();
    group.name = DEFAULT_FILL_CORNER_DEBUG_GROUP;

    const marker = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xff2d2d, depthTest: false })
    );
    marker.position.set(corner.x, corner.z, 0.04);
    marker.renderOrder = 9999;
    group.add(marker);

    const roofNum = roofMesh?.userData?.roof_tag_id ?? roofMesh?.userData?.roof_number_from_aurora ?? roofMesh?.roofFaceId ?? '?';
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`R${roofNum} START`, canvas.width / 2, canvas.height / 2);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
    }));
    sprite.position.set(corner.x, corner.z, 0.55);
    sprite.scale.set(2.4, 0.6, 1);
    sprite.renderOrder = 10000;
    group.add(sprite);
    roofMesh.add(group);
}

function toggleRoofEdgeTypeLabels(self: Viewer, args: { show: boolean }) {
    const show = !!args?.show;
    self.edgeTypeTagGroup.clear();
    self.edgeTypeTagGroup.visible = show;
    if (!show) return;

    self.roofs?.[0]?.edges?.forEach((edge: any) => {
        const p0 = edge?.posArr?.[0];
        const p1 = edge?.posArr?.[1];
        if (!p0 || !p1) return;
        const midPoint = new THREE.Vector3((p0.x + p1.x) / 2, (p0.y + p1.y) / 2, (p0.z + p1.z) / 2);

        const label = document.createElement('div');
        label.className = 'roofTagLabel';
        label.textContent = `${String(edge?.type || 'other').toUpperCase()}`;

        const labelTag = new CSS2DObject(label);
        labelTag.position.copy(midPoint);
        labelTag.position.y += 0.5;
        labelTag.name = 'edgeTagLabel';
        labelTag.visible = true;
        self.edgeTypeTagGroup.add(labelTag);
    });
}

function drawBoundMeshBox(self: Viewer, args: { boundMesh: any; group?: string }) {
    const boundMesh = args?.boundMesh;
    if (!boundMesh) return;
    const group = args?.group || 'bound-mesh';
    ClearDebugDraw(self, group);
    const box = new THREE.Box3().setFromObject(boundMesh);
    DebugDrawBox(self, { box, color: 0xff0000, opacity: 0.12, group });
}

function drawObstructionCenterPoint(self: Viewer, args: { position: any; group?: string }) {
    const p = args?.position;
    if (!p) return;
    DebugDrawPoint(self, {
        position: new THREE.Vector3(p.x, p.y, p.z),
        radius: 0.08,
        color: '#ff0000',
        group: args?.group || 'obstruction-centers',
    });
}

function drawObstructionNodes(self: Viewer, args: { nodes: any[]; group?: string }) {
    const nodes = args?.nodes || [];
    const group = args?.group || 'obstruction-nodes';
    nodes.forEach((node: any) => {
        DebugDrawPoint(self, {
            position: new THREE.Vector3(node.X ?? node.x ?? 0, node.Y ?? node.y ?? 0, node.Z ?? node.z ?? 0),
            radius: 0.06,
            color: '#0000ff',
            group,
        });
    });
}

function drawObstructionCentersFromScene(self: Viewer, args?: { group?: string }) {
    const group = args?.group || 'obstruction-centers';
    ClearDebugDraw(self, group);
    self.obstGroup?.children?.forEach((obstObj: any) => {
        const obstMesh = obstObj?.children?.[0]?.children?.[0]?.children?.[0];
        if (!obstMesh) return;
        const bbox = new THREE.Box3().setFromObject(obstMesh);
        const centerTop = new THREE.Vector3(
            (bbox.min.x + bbox.max.x) / 2,
            bbox.max.y,
            (bbox.min.z + bbox.max.z) / 2
        );
        DebugDrawPoint(self, { position: centerTop, radius: 0.08, color: '#ff0000', group });
    });
}

function drawObstructionNodesFromScene(self: Viewer, args?: { group?: string }) {
    const group = args?.group || 'obstruction-nodes';
    ClearDebugDraw(self, group);
    self.obstGroup?.children?.forEach((obstObj: any) => {
        const obstMesh = obstObj?.children?.[0]?.children?.[0]?.children?.[0];
        const nodes = obstMesh?.verPosArr;
        if (!Array.isArray(nodes) || !nodes.length) return;
        nodes.forEach((node: any) => {
            const x = node.x ?? node.X ?? 0;
            const y = node.y ?? node.Y ?? obstMesh.getWorldPosition(new THREE.Vector3()).y;
            const z = node.z ?? node.Z ?? 0;
            DebugDrawPoint(self, {
                position: new THREE.Vector3(x, y, z),
                radius: 0.06,
                color: '#0000ff',
                group,
            });
        });
    });
}

function drawRoofNodesFromScene(self: Viewer, args?: { group?: string }) {
    const group = args?.group || 'roof-nodes';
    ClearDebugDraw(self, group);

    const building = self.roofs?.[0];
    if (!building?.faces?.length) return;

    const drawnNodeIndices = new Set<number>();

    building.faces.forEach((face: any) => {
        const shell = face?.shell;
        const posArr = face?.posArr;
        if (!Array.isArray(shell) || !Array.isArray(posArr)) return;

        shell.forEach((nodeIndex: number, shellIdx: number) => {
            if (drawnNodeIndices.has(nodeIndex)) return;
            const pos = posArr[shellIdx];
            if (!pos) return;
            drawnNodeIndices.add(nodeIndex);

            const position = new THREE.Vector3(pos.x, pos.y, pos.z);
            DebugDrawPoint(self, {
                position,
                radius: 0.08,
                color: '#00aa00',
                group,
            });

            const label = document.createElement('div');
            label.className = 'roofTagLabel';
            label.textContent = String(nodeIndex);

            const labelTag = new CSS2DObject(label);
            labelTag.position.copy(position);
            labelTag.position.y += 0.35;
            labelTag.userData.debugDraw = true;
            labelTag.userData.group = group;
            self.scene.add(labelTag);
        });
    });
}

function toggleTreeCylinders(self: Viewer, args: { show: boolean }) {
    if (!self?.treeGroup?.children) return;
    const show = !!args?.show;
    self.treeGroup?.children?.forEach((tree: any) => {
        const wrapperMesh = tree?.children?.[2];
        if (!wrapperMesh?.material) return;
        wrapperMesh.visible = show;
        wrapperMesh.material.color?.setHex?.(0xffffff);
        wrapperMesh.material.needsUpdate = true;
    });
}

function drawModuleCollisionObb(self: Viewer, args: { object: any; isHit?: boolean; group?: string }) {
    const object = args?.object;
    if (!object) return;
    DebugDrawOBB(self, {
        object,
        opacity: 0.3,
        color: args?.isHit ? 0x0000ff : 0xff0000,
        group: args?.group || 'module-collision',
    });
}

function drawWallObb(self: Viewer, args: { object: any; group?: string }) {
    const object = args?.object;
    if (!object) return;
    DebugDrawOBB(self, {
        object,
        opacity: 0.25,
        color: 0xff9800,
        group: args?.group || 'wall-obb',
    });
}

function drawRayArrow(self: Viewer, args: { origin: THREE.Vector3; direction: THREE.Vector3; length?: number; color?: number; group?: string }) {
    const origin = args?.origin;
    const direction = args?.direction;
    if (!origin || !direction) return;
    const arrow = new THREE.ArrowHelper(
        direction.clone().normalize(),
        origin.clone(),
        args?.length ?? 200,
        args?.color ?? 0xff0000
    );
    arrow.userData.debugDraw = true;
    arrow.userData.group = args?.group || 'obstruction-ray-arrows';
    self.scene.add(arrow);
}

function clearDebugGroup(self: Viewer, args: { group: string }) {
    const group = args?.group;
    if (!group) return;
    ClearDebugDraw(self, group);
}

function drawWallObbFromScene(self: Viewer, args?: { group?: string }) {
    const group = args?.group || 'wall-obb';
    ClearDebugDraw(self, group);
    const walls = self.wallMeshArr || [];
    walls.forEach((wall: any) => {
        if (!wall) return;
        drawWallObb(self, { object: wall, group });
    });
}

const debugManagerRegistry: Record<string, DebugManagerFn> = {
    clearDefaultFillCorner,
    renderDefaultFillStartCorner,
    toggleRoofEdgeTypeLabels,
    drawBoundMeshBox,
    drawObstructionCenterPoint,
    drawObstructionNodes,
    drawObstructionCentersFromScene,
    drawObstructionNodesFromScene,
    drawRoofNodesFromScene,
    toggleTreeCylinders,
    drawModuleCollisionObb,
    drawWallObb,
    drawRayArrow,
    clearDebugGroup,
    drawWallObbFromScene,
};

export const DebugDrawManager = {
    execute(self: Viewer, functionName: string, args?: any) {
        if (!self) return;
        const fn = debugManagerRegistry[functionName];
        if (!fn) return;
        fn(self, args);
    },
};

export function isOneDrawDebugMode(): boolean {
    if (typeof import.meta === 'undefined') return false;
    return import.meta.env.MODE === 'debug' || import.meta.env[DEBUG_FLAG] === 'true';
}

export function getOneDrawDebuggerOptions(): OneDrawDebuggerOptions {
    if (typeof window === 'undefined') return defaultDebuggerOptions;

    const winOptions = (window as any).__ONEDRAW_DEBUGGER_OPTIONS__;
    return {
        ...defaultDebuggerOptions,
        ...(winOptions || {}),
        fillDebugger: {
            ...defaultDebuggerOptions.fillDebugger,
            ...(winOptions?.fillDebugger || {}),
        },
        shadeWorker: {
            ...defaultDebuggerOptions.shadeWorker,
            ...(winOptions?.shadeWorker || {}),
        },
    };
}

export function setOneDrawDebuggerOptions(options: Partial<OneDrawDebuggerOptions>) {
    if (typeof window === 'undefined') return;

    const merged: OneDrawDebuggerOptions = {
        ...getOneDrawDebuggerOptions(),
        ...options,
        fillDebugger: {
            ...getOneDrawDebuggerOptions().fillDebugger,
            ...(options.fillDebugger || {}),
        },
        shadeWorker: {
            ...getOneDrawDebuggerOptions().shadeWorker,
            ...(options.shadeWorker || {}),
        },
    };

    (window as any).__ONEDRAW_DEBUGGER_OPTIONS__ = merged;
    window.dispatchEvent(new CustomEvent(ONEDRAW_DEBUGGER_VISIBILITY_EVENT, { detail: merged }));
}
